import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ToastComponent } from "../../../../shared/components/toast/toast.component";
import { CommonModule } from '@angular/common';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { Router } from '@angular/router';
import { CustomButtonComponent } from "../../../../shared/components/custom-button/custom-button.component";
import { AdminService } from '../../../../core/api/admin.service';
import { lastValueFrom } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { ApiErrorService } from '../../../../core/api/api-error.service';
import { FileExtractionEventLogGet, FileExtractionGet } from '../../../../core/interfaces/admin-interfaces';
import { IsoToFormattedDatePipe } from "../../../../core/pipes/iso-to-formatted-date.pipe";
import { ExternalFileStatus } from '../../../../core/interfaces/file-upload-interfaces';
import { animate, style, transition, trigger } from '@angular/animations';
import { ExpandedExtractionHistoryComponent } from "../../components/expanded-extraction-history/expanded-extraction-history.component";
import { AdminNavigationComponent } from "../../components/admin-navigation/admin-navigation.component";
import { AdminTopbarComponent } from "../../components/admin-topbar/admin-topbar.component";

@Component({
  selector: 'app-admin-file-extraction-history',
  standalone: true,
  imports: [ToastComponent, CommonModule, TranslocoPipe, CustomButtonComponent, IsoToFormattedDatePipe, ExpandedExtractionHistoryComponent, AdminNavigationComponent, AdminTopbarComponent],
  templateUrl: './admin-file-extraction-history.component.html',
  styleUrl: './admin-file-extraction-history.component.css',
  animations: [
    trigger('expandRow', [
      transition(':enter', [
        style({ height: '0px', opacity: 0 }),
        animate('500ms ease-out', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ height: '0px', opacity: 0 }))
      ])
    ])
  ]
})
export class AdminFileExtractionHistoryComponent implements OnInit {
  constructor(private router: Router, private adminService: AdminService, private translocoService: TranslocoService, private apiError: ApiErrorService) { }

  // Toast
  @ViewChild(ToastComponent) toast!: ToastComponent;

  // File Upload
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  selectedFile?: File;

  isUploading: boolean = false;

  // Load History
  extractionHistory: FileExtractionGet[] = [];
  loadedExtractionHistory: FileExtractionGet[] = [];

  lastLoadedPageNumber: number = 0;
  loadMoreHidden = false;
  historyFetchedCount: number = 0;

  loadedEventLogs?: FileExtractionEventLogGet[];

  // Expanded Row Id
  expandedRow: number | null = null;

  ngOnInit(): void {
    this.reloadHistory();
  }

  goToDashboard() {
    this.router.navigate(["/dashboard"]);
  }

  triggerFileInput() {
    this.fileInput.nativeElement.click();
  }

  async onFileSelected(event: Event): Promise<void> {
    const fileInput = event.target as HTMLInputElement;
    const files = fileInput?.files;

    if (!files || files.length === 0) return;
    const file = files[0];

    try {
      this.isUploading = true;
      await lastValueFrom(this.adminService.uploadFileForExtraction(file));
      this.toast.showToast(this.translocoService.translate("shared.file_upload.successful"), this.translocoService.translate("admin.file_uploaded", { fileName: file.name }), "success", 4000, false);
    } catch (error) {
      var showError = true;
      let crid = 'UNKNOWN-CRID';

      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
      }

      if (showError) {
        this.toast.showToast('shared.warning', 'admin.not_fully_completed', 'warning', 8000, true);
      }
    }

    // Reset file input and allow selectin new files
    fileInput.value = '';

    this.reloadHistory();
    this.isUploading = false;
  }

  loadHistoryNewPage() {
    this.adminService.getFileExtractionHistory((this.lastLoadedPageNumber + 1)).subscribe({
      next: (response) => {
        if (response) {
          if (response.data.length === 0 || this.historyFetchedCount + response.data.length >= response.totalCount) {
            this.loadMoreHidden = true;
          }
          this.historyFetchedCount += response.data.length;

          this.loadedExtractionHistory = [...this.loadedExtractionHistory, ...response.data]

          // Applay filter
          this.resetHistoryFilter();
        }
      },
      error: async (error) => {
        var crid = 'UNKNOWN-CRID';

        if (error instanceof HttpErrorResponse) {
          crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
        }
        this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      },
      complete: () => {
        this.lastLoadedPageNumber++;
      }
    });
  }

  resetHistoryFilter() {
    this.extractionHistory = [];
    this.extractionHistory = this.loadedExtractionHistory;
    this.expandedRow = null;
  }

  reloadHistory = () => {
    this.loadedExtractionHistory = [];
    this.extractionHistory = [];
    this.loadMoreHidden = false;
    this.lastLoadedPageNumber = 0;
    this.historyFetchedCount = 0;
    this.loadHistoryNewPage();
    this.resetHistoryFilter();
    this.expandedRow = null;
  };

  getExternalStatusTranslocoKey(status: string | null): string {
    const validStatuses = Object.values(ExternalFileStatus);
    const safeStatus = validStatuses.includes(status as ExternalFileStatus) ? status : 'PENDING';
    return `archive.external_file_status.${safeStatus}`;
  }

  async toggleDetails(index: number): Promise<void> {
    this.expandedRow = this.expandedRow === index ? null : index;

    if (this.expandedRow != null) {
      try {
        const response = await lastValueFrom(this.adminService.getExtractionEventLogs(this.extractionHistory[index].id));
        this.loadedEventLogs = [];
        this.loadedEventLogs = response;
      } catch (error) {
        var showError = true;
        let crid = 'UNKNOWN-CRID';

        if (error instanceof HttpErrorResponse) {
          crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
        }

        if (showError) {
          this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
        }
      }

    }
  }
}
